import {
  Col,
  Pagination,
  PaginationItem,
  PaginationLink,
  Row,
} from "reactstrap";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Spotlight, spotlight } from "@mantine/spotlight";
import { atomWithStorage } from "jotai/utils";
import "@mantine/spotlight/styles.css";
import { Tag, TagGroup } from "rsuite";
import { useAtomValue } from "jotai";
import { useAtom } from "jotai";

import ThumbnailWidget from "../components/Widgets/ThumbnailWidget/ThumbnailWidget";
import ViewRecordDrawer from "../components/Drawers/Records/ViewRecordDrawer";
import useCloudSearch from "../api/api-hooks/cloud/useCloudSearch";
import { modulesAtom, tenantIDAtom } from "../atoms/GlobalAtoms";
import useCloudPermissions from "../hooks/useCloudPermissions";
import { Sections } from "../views/cloud/CloudSettingsHome";
import { workspaceIdAtom } from "../atoms/WorkspaceAtoms";
import { Module } from "../types/TenantInterfaces";
import AppSpinner from "../elements/AppSpinner";
import TenantAPI from "../api/api/TenantAPI";
import StrUtils from "../utils/StrUtils";

const moduleFiltersAtom = atomWithStorage<any[]>("moduleFilters", []);

const initialModuleFilters = [
  {
    APIName: "Contacts",
    Name: "Contacts",
    active: true,
  },
  {
    APIName: "Companies",
    Name: "Companies",
    active: true,
  },
  {
    APIName: "Activities",
    Name: "Activities",
    active: true,
  },
  {
    APIName: "Feed",
    Name: "Feed",
    active: false,
  },
];

const searchIcon = <i className="mdi mdi-magnify" />;
const searchPlaceholder = "Search Cloud...";
const nothingFoundMessage = "No results found...";
const transitionDuration = 150;
const transition = "scale";

const AppSpotlight = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const modules = useAtomValue(modulesAtom);
  const currentCloud = useAtomValue(tenantIDAtom);
  const workspaceID = useAtomValue(workspaceIdAtom);
  const { cPermissions } = useCloudPermissions();
  const [searchedValue, setSearchedValue] = useState("");
  const [loading, setLoading] = useState(false);
  const [openRecordData, setOpenRecordData] = useState(false);
  const [viewRecordID, setViewRecordID] = useState(null);
  const [viewRecordModule, setViewRecordModule] = useState<Module | null>(null);
  const [actions, setActions] = useState<any>(null);
  const [numberActions, setNumberActions] = useState(0);
  const [moduleFilters, setModuleFilters] = useAtom(moduleFiltersAtom || []);
  const [pagination, setPagination] = useState({
    PreviousPage: null,
    HasPreviousPage: false,
    CurrentPage: 1,
    NextPage: 2,
    HasNextPage: true,
    ResultRecordsMin: 0,
    ResultRecordsMax: 25,
    RecordsCount: 25,
    TotalRecords: 0,
  });

  const shortcuts = !location.pathname.includes("supportal")
    ? ["mod + K"]
    : null;

  const { cloudSearchAction } = useCloudSearch();

  // Get all the settings options
  const settingsActions = () => {
    const sections = Sections(cPermissions);
    return sections
      .filter((section) => !section.disabled)
      .map((section) => {
        return {
          ...section,
          onTrigger: () => navigate(`/clouds/${currentCloud}/${section.link}`),
          group: "Settings",
          icon: (
            <i
              className={`${section.icon} text-dark`}
              style={{ fontSize: 22 }}
            />
          ),
        };
      });
  };

  const defaultActions = [
    {
      title: "Home",
      description: "Go to workspace home page",
      onTrigger: () =>
        workspaceID
          ? navigate(
              `/clouds/${currentCloud}/workspaces/${workspaceID}/home/pages`
            )
          : navigate(`/clouds/${currentCloud}/workspaces`),
      icon: <i className="mdi mdi-home text-dark" style={{ fontSize: 22 }} />,
    },
    {
      title: "Workspaces",
      description: "View all workspaces",
      onTrigger: () => navigate(`/clouds/${currentCloud}/workspaces`),
      icon: (
        <i
          className="mdi mdi-view-dashboard-outline text-dark"
          style={{ fontSize: 22 }}
        />
      ),
    },
    {
      title: "Cloud Home",
      description: "View recent activity in the cloud home page",
      onTrigger: () => navigate(`/clouds/${currentCloud}/activity`),
      icon: (
        <i
          className="mdi mdi-cloud-outline text-dark"
          style={{ fontSize: 22 }}
        />
      ),
    },
  ];

  const onChange = (newSearchValue: string) => {
    setActions(null);
    setSearchedValue(newSearchValue);
  };

  const handleTriggerRecord = (result: any) => {
    if (!modules) return;

    setOpenRecordData(true);
    setViewRecordID(result._id);

    const recordModule = modules.find(
      (m) => m.APIName === result.moduleAPIName
    );

    if (!recordModule) return;

    setViewRecordModule(recordModule);
  };

  const getThumbnailRecord = async (
    moduleAPIName: string,
    recordID: string
  ) => {
    const record = await TenantAPI.getRecordByID(moduleAPIName, recordID).then(
      (data) => {
        return data.data.data.data;
      }
    );
    return record;
  };

  const convertSearchResults = async (searchResults: any) => {
    // Adds onTrigger property to all actions depending on the group
    for (const result of searchResults) {
      if (result.group === "Workspace") {
        result.onTrigger = () =>
          navigate(
            `/clouds/${currentCloud}/workspaces/${result._id}/home/pages`
          );
        result.icon = <i className="mdi mdi-view-dashboard-outline" />;
      }
      if (result.group === "Module") {
        result.onTrigger = () =>
          navigate(`/clouds/${currentCloud}/records/${result.APIName}`);

        result.icon = <i className="mdi mdi-database-outline" />;
      }
      if (result.group === "Page") {
        result.onTrigger = () =>
          navigate(
            `/clouds/${currentCloud}/workspaces/${workspaceID}/pages/${result._id}/views/${result.viewID}`
          );
        result.icon = <i className="mdi mdi-file-table-box-outline" />;
      }
      if (result.group === "Record") {
        result.onTrigger = () => handleTriggerRecord(result);
        if (
          result.moduleAPIName === "Companies" ||
          result.moduleAPIName === "Contacts"
        ) {
          const thumbnailRecord = await getThumbnailRecord(
            result.moduleAPIName,
            result._id
          );

          if (thumbnailRecord) {
            result.icon = (
              <ThumbnailWidget
                record={thumbnailRecord}
                mode={result.moduleAPIName === "Contacts" ? "Avatar" : "Logo"}
                size="md"
                imageKey={undefined}
              />
            );
          } else {
            result.icon = <i className="mdi mdi-record" />;
          }
        } else {
          result.icon = <i className="mdi mdi-record" />;
        }
      }
    }

    // Adds Settings actions (All options in /settings)
    const settingsActionsResults = settingsActions();
    const filteredSettingsActions = settingsActionsResults.filter(
      (settingAction) =>
        settingAction.title
          .toLocaleLowerCase()
          .includes(searchedValue.toLowerCase())
    );

    const results = orderActions(searchResults);

    if (Object.keys(results).length > 0 || filteredSettingsActions.length > 0)
      results.Settings = filteredSettingsActions;

    setNumberActions(searchResults.length);

    setActions(results);
    setLoading(false);
  };

  const handleChangeFilter = (moduleFilter: any) => {
    setModuleFilters((prev) =>
      prev.map((module) => {
        if (module.APIName === moduleFilter.APIName) {
          return {
            ...module,
            active: !moduleFilter.active,
          };
        }

        return module;
      })
    );
    setPagination({
      PreviousPage: null,
      HasPreviousPage: false,
      CurrentPage: 1,
      NextPage: 2,
      HasNextPage: true,
      ResultRecordsMin: 0,
      ResultRecordsMax: 25,
      RecordsCount: 25,
      TotalRecords: 0,
    });
  };

  // Determines if all module filters are active
  const allFiltersActive = useMemo(() => {
    return moduleFilters.every((filter) => filter.active === true);
  }, [moduleFilters]);

  const handleAllFilters = () => {
    setModuleFilters((prev) =>
      prev.map((item) => ({ ...item, active: !allFiltersActive }))
    );
    setPagination({
      PreviousPage: null,
      HasPreviousPage: false,
      CurrentPage: 1,
      NextPage: 2,
      HasNextPage: true,
      ResultRecordsMin: 0,
      ResultRecordsMax: 25,
      RecordsCount: 25,
      TotalRecords: 0,
    });
  };

  const convertSearchResultsRef = useRef<any>();
  convertSearchResultsRef.current = convertSearchResults;

  const orderActions = (results: any) => {
    let groupedActions: any = {};
    results.forEach((item: any) => {
      if (!groupedActions[item.group]) {
        groupedActions[item.group] = [];
      }

      groupedActions[item.group].push(item);
    });

    return groupedActions;
  };

  const handleCloseSpotlight = () => {
    spotlight.close();
    setActions({});
    setNumberActions(0);
    setSearchedValue("");
    setPagination({
      PreviousPage: null,
      HasPreviousPage: false,
      CurrentPage: 1,
      NextPage: 2,
      HasNextPage: true,
      ResultRecordsMin: 0,
      ResultRecordsMax: 25,
      RecordsCount: 25,
      TotalRecords: 0,
    });
  };

  // Makes API request on change
  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      if (searchedValue && moduleFilters && pagination) {
        cloudSearchAction({ searchedValue, moduleFilters, pagination }).then(
          (data) => {
            convertSearchResultsRef.current(data.data.data.actions);
            setPagination(data.data.data.pagination);
          }
        );
      } else {
        setLoading(false);
      }
    }, 1000);

    return () => {
      clearTimeout(delayDebounceFn);
    };
  }, [searchedValue, moduleFilters]);

  useEffect(() => {
    if (!moduleFilters || moduleFilters.length < 1) {
      setModuleFilters(initialModuleFilters);
    }
  }, [modules]);

  const loadNextOrPreviousPage = async (e: any, key: any) => {
    e.preventDefault();

    let updatedPagination;
    if (key === "PreviousPage")
      updatedPagination = {
        ...pagination,
        CurrentPage: pagination.CurrentPage - 1,
      };

    if (key === "NextPage") {
      updatedPagination = {
        ...pagination,
        CurrentPage: pagination.CurrentPage + 1,
      };
    }

    await cloudSearchAction({
      searchedValue,
      moduleFilters,
      pagination: updatedPagination,
    }).then((data) => {
      convertSearchResultsRef.current(data.data.data.actions);
      setPagination(data.data.data.pagination);
    });
  };

  // Spotlight component that contains all actions
  const ActionsWrapperComponent = ({
    children,
  }: {
    children: React.ReactNode;
  }) => {
    return (
      <>
        <div>
          <TagGroup className="py-2 px-4">
            <Tag
              key="All"
              size="md"
              style={{
                borderRadius: "4px",
                backgroundColor: allFiltersActive ? "#C8F3D8" : "#E8EDEA",
              }}
              onClick={() => handleAllFilters()}
              className="cursor-pointer"
            >
              All
            </Tag>

            {moduleFilters.map((moduleFilter) => {
              return (
                <Tag
                  key={moduleFilter.APIName}
                  size="md"
                  style={{
                    borderRadius: "4px",
                    backgroundColor: moduleFilter.active
                      ? "#C8F3D8"
                      : "#E8EDEA",
                  }}
                  onClick={() => handleChangeFilter(moduleFilter)}
                  className="cursor-pointer"
                >
                  {moduleFilter.Name}
                </Tag>
              );
            })}
          </TagGroup>
          {children}
        </div>
        {searchedValue && (
          <div className="border-top px-3 py-2 py-sm-3 pr-4">
            <Row className="align-items-center">
              <Col xs="12" sm="">
                <div className="d-inline-block mb-3 mb-sm-0">
                  {loading ? (
                    <>
                      <small>Loading records...</small>
                    </>
                  ) : (
                    <>
                      {pagination && numberActions > 0 && (
                        <small>
                          Viewing &nbsp;
                          {StrUtils.numberFormat(
                            pagination.ResultRecordsMin
                          )} -{" "}
                          {numberActions > pagination.RecordsCount
                            ? StrUtils.numberFormat(pagination.ResultRecordsMax)
                            : numberActions}{" "}
                          of {StrUtils.numberFormat(pagination.TotalRecords)}{" "}
                          {"actions"}
                        </small>
                      )}
                    </>
                  )}
                </div>
              </Col>
              <Col xs="12" sm="auto">
                <div className="d-inline-block ml-3">
                  <nav aria-label="Page navigation example">
                    <Pagination
                      className="pagination justify-content-end mb-0"
                      listClassName="justify-content-end mb-0"
                      size="sm"
                    >
                      <PaginationItem
                        className={`${
                          pagination?.HasPreviousPage ? "" : "disabled"
                        }`}
                      >
                        <PaginationLink
                          href="#"
                          onClick={(e) =>
                            loadNextOrPreviousPage(e, "PreviousPage")
                          }
                          tabIndex={-1}
                        >
                          <i className="mdi mdi-chevron-left" />
                          <span className="sr-only">Previous</span>
                        </PaginationLink>
                      </PaginationItem>
                      <PaginationItem
                        className={`${pagination?.NextPage ? "" : "disabled"}`}
                      >
                        <PaginationLink
                          href="#"
                          onClick={(e) => loadNextOrPreviousPage(e, "NextPage")}
                        >
                          <i className="mdi mdi-chevron-right" />
                          <span className="sr-only">Next</span>
                        </PaginationLink>
                      </PaginationItem>
                    </Pagination>
                  </nav>
                </div>
              </Col>
            </Row>
          </div>
        )}
      </>
    );
  };

  const actionKeys = useMemo(() => {
    if (!actions) return null;
    return Object.keys(actions);
  }, [actions]);

  return (
    <>
      <Spotlight.Root
        query={searchedValue}
        onQueryChange={onChange}
        shortcut={shortcuts}
        transitionProps={{
          transition: transition,
          duration: transitionDuration,
        }}
        onSpotlightClose={() => handleCloseSpotlight()}
      >
        <Spotlight.Search
          placeholder={searchPlaceholder}
          leftSection={searchIcon}
        />
        <ActionsWrapperComponent>
          {loading ? (
            <div className="d-flex w-100 justify-content-center align-items-center p-3">
              <AppSpinner size="lg" />
            </div>
          ) : (
            <Spotlight.ActionsList>
              <div className="action-container">
                {!actionKeys ? (
                  <>
                    {defaultActions.map((action: any, index: number) => {
                      return (
                        <Spotlight.Action
                          key={index}
                          label={action.title}
                          leftSection={action.icon}
                          title={action.title}
                          description={action.description}
                          onClick={() => action.onTrigger()}
                        />
                      );
                    })}
                  </>
                ) : actionKeys?.length < 1 ? (
                  <p className="text-center text-light mt-3">
                    No results found...
                  </p>
                ) : (
                  <>
                    {actionKeys.map((actionGroup: string, index: number) => {
                      const groupActions = actions[actionGroup];

                      return (
                        <Spotlight.ActionsGroup key={index} label={actionGroup}>
                          {groupActions.map((action: any) => {
                            return (
                              <Spotlight.Action
                                key={action.id}
                                label={action.title}
                                leftSection={action.icon}
                                title={action.title}
                                description={action.description}
                                onClick={() => action.onTrigger()}
                              />
                            );
                          })}
                        </Spotlight.ActionsGroup>
                      );
                    })}
                  </>
                )}
              </div>
            </Spotlight.ActionsList>
          )}
        </ActionsWrapperComponent>
      </Spotlight.Root>

      {openRecordData && viewRecordModule && viewRecordID && (
        <ViewRecordDrawer
          open={!!viewRecordID}
          module={viewRecordModule}
          recordID={viewRecordID}
          onClose={() => setViewRecordID(null)}
          onExited={() => setOpenRecordData(false)}
        />
      )}
    </>
  );
};

export default AppSpotlight;
